var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * React-query hooks for the vocabulary derivation rule admin GUI (CZNA-365,
 * epic CZNA-297, child 3/4).
 *
 * Wraps `vocabularyDerivationRulesService` so components stay free of `ky`
 * plumbing and cache invalidation is centralised. Mirrors
 * `useValidationRules.ts`. Keyed on the query params object so the unstable
 * `useOkapiKy()` reference inside the closure never retriggers the query (see
 * MEMORY / ui-czna-development skill).
 */
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { fetchVocabularyDerivationRules, fetchVocabularyDerivationRule, createVocabularyDerivationRule, updateVocabularyDerivationRule, deleteVocabularyDerivationRule, fetchVocabularyDerivationRuns, deriveVocabularyDerivationRule, } from '../api/vocabularyDerivationRulesService';
var RULES_KEY = 'vocabulary-derivation-rules';
var RUNS_KEY = 'vocabulary-derivation-runs';
/** List of all rules, ordered by vocabularyType. Small registry — no paging. */
export var useVocabularyDerivationRules = function () {
    var ky = useOkapiKy();
    return useQuery([RULES_KEY], function () { return fetchVocabularyDerivationRules(ky); }, { keepPreviousData: true });
};
/**
 * Single rule detail. Disabled until an id is selected.
 *
 * `staleTime`/`refetchOnWindowFocus: false` are defense-in-depth only — the
 * real guard against a background refetch clobbering an admin's in-progress
 * unsaved edits is `DerivationRuleDetail`'s id-keyed seed effect (CZNA-381
 * code review), not these query options. Explicit `useSaveVocabularyDerivationRule`
 * invalidation still refreshes this query immediately after a save.
 */
export var useVocabularyDerivationRule = function (id) {
    var ky = useOkapiKy();
    return useQuery([RULES_KEY, 'detail', id], function () { return fetchVocabularyDerivationRule(ky, id); }, { enabled: !!id, staleTime: 30 * 1000, refetchOnWindowFocus: false });
};
/**
 * Create (POST) or update (PUT) a rule. The mutation discriminates on `mode`
 * so a single hook backs both the create-new and edit-existing flows.
 * Invalidates the list + detail family on success. Callers MUST supply
 * `onError` (project rule).
 */
export var useSaveVocabularyDerivationRule = function (options) {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    return useMutation(function (args) {
        return args.mode === 'create'
            ? createVocabularyDerivationRule(ky, args.body)
            : updateVocabularyDerivationRule(ky, args.id, args.body);
    }, __assign(__assign({}, options), { onSuccess: function (data, vars, ctx) {
            var _a;
            queryClient.invalidateQueries([RULES_KEY]);
            (_a = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _a === void 0 ? void 0 : _a.call(options, data, vars, ctx);
        } }));
};
/**
 * Delete a rule. Invalidates the list family on success.
 */
export var useDeleteVocabularyDerivationRule = function (options) {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    return useMutation(function (id) { return deleteVocabularyDerivationRule(ky, id); }, __assign(__assign({}, options), { onSuccess: function (data, vars, ctx) {
            var _a;
            queryClient.invalidateQueries([RULES_KEY]);
            (_a = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _a === void 0 ? void 0 : _a.call(options, data, vars, ctx);
        } }));
};
/**
 * Run audit trail — global or per-rule (pass `ruleId` in `params`). Plain
 * array response, no total count; `keepPreviousData` for smooth paging.
 */
export var useVocabularyDerivationRuns = function (params, enabled) {
    if (enabled === void 0) { enabled = true; }
    var ky = useOkapiKy();
    return useQuery([RUNS_KEY, params], function () { return fetchVocabularyDerivationRuns(ky, params); }, { enabled: enabled, keepPreviousData: true });
};
/**
 * "Derive now" mutation — POST derive (dry-run or real).
 *
 * Invalidation scope (code review S3):
 * - `[RUNS_KEY]` is invalidated UNCONDITIONALLY, for both dry-run and real
 *   calls. The backend (`VocabularyDerivationRuleAdminService#derive`) always
 *   writes a `vocabulary_derivation_run` audit row, including for dry runs
 *   and even when the derive call throws — so there is always a new run row
 *   to show in the run-history list regardless of `dryRun`.
 * - `[RULES_KEY]` is invalidated ONLY for a real (non-dry-run) derive: a real
 *   run can create new vocabulary terms / change observable rule state,
 *   while a dry-run makes no DB writes at all, so invalidating the rule list
 *   on a dry-run would just be a wasted refetch.
 *
 * Callers MUST supply `onError` (project rule).
 */
export var useDeriveVocabularyDerivationRule = function (options) {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    return useMutation(function (_a) {
        var ruleId = _a.ruleId, dryRun = _a.dryRun;
        return deriveVocabularyDerivationRule(ky, ruleId, dryRun);
    }, __assign(__assign({}, options), { onSuccess: function (data, vars, ctx) {
            var _a;
            queryClient.invalidateQueries([RUNS_KEY]);
            if (!vars.dryRun) {
                queryClient.invalidateQueries([RULES_KEY]);
            }
            (_a = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _a === void 0 ? void 0 : _a.call(options, data, vars, ctx);
        } }));
};
